/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/SOPCompliance/SOPComplianceDashboard.tsx
 * PURPOSE: SOP Compliance Dashboard - 100% NQH Brand Design System Compliant
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-26
 * DESIGN: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System V2.0 + Vietnamese BPM
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: SOP Compliance Analytics, KPI Tracking, Multi-Industry BPM
 * - Impact: Revenue Critical (₫38B TAM), Operations Dashboard
 * - Stakeholders: Operations Team, Management, Local Team (Vietnam)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Dashboard Page
 * - Pattern: CSS Modules, Design Tokens, React 18
 * - Dependencies: React Router, TypeScript, nqh-design-tokens.css
 *
 * @cross-references
 * - Design: docs/02-design/10-bpm-architecture/SOP-IMPORT-COMPLIANCE-KPI-DESIGN.md
 * - API: backend/app/api/v1/sop_analytics.py
 * - Service: frontend/src/services/api/sopComplianceService.ts
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/auth';
import { getTenantDisplayText } from '../../config/tenant';
import {
  sopComplianceService,
  ComplianceDashboardResponse,
  ComplianceTrendsResponse,
  IndustryCoverageResponse,
  SOPPerformer,
  KPISummary,
  INDUSTRY_NAMES_VI,
  getComplianceScoreStatus,
  formatPercent,
  formatDateVN,
} from '../../services/api/sopComplianceService';
import styles from './SOPComplianceDashboard.module.css';

interface DashboardState {
  selectedIndustry: string;
  trendDays: number;
  viewMode: 'overview' | 'trends' | 'industries' | 'sops';
}

type TrendDirection = 'increasing' | 'decreasing' | 'stable';

export const SOPComplianceDashboard: React.FC = () => {
  const { user, logout } = useAuthStore();

  const [state, setState] = useState<DashboardState>({
    selectedIndustry: 'all',
    trendDays: 30,
    viewMode: 'overview',
  });

  const [dashboard, setDashboard] = useState<ComplianceDashboardResponse | null>(null);
  const [trends, setTrends] = useState<ComplianceTrendsResponse | null>(null);
  const [coverage, setCoverage] = useState<IndustryCoverageResponse | null>(null);
  const [topSOPs, setTopSOPs] = useState<SOPPerformer[]>([]);
  const [bottomSOPs, setBottomSOPs] = useState<SOPPerformer[]>([]);
  const [kpiSummary, setKpiSummary] = useState<KPISummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const industry = state.selectedIndustry !== 'all' ? state.selectedIndustry : undefined;

        const [dashboardData, trendsData, coverageData, topData, bottomData, kpiData] = await Promise.all([
          sopComplianceService.getDashboard(industry),
          sopComplianceService.getTrends(state.trendDays, industry),
          sopComplianceService.getIndustryCoverage(),
          sopComplianceService.getTopSOPs(5),
          sopComplianceService.getBottomSOPs(5),
          sopComplianceService.getKPISummary(industry),
        ]);

        setDashboard(dashboardData);
        setTrends(trendsData);
        setCoverage(coverageData);
        setTopSOPs(topData);
        setBottomSOPs(bottomData);
        setKpiSummary(kpiData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Lỗi khi tải dữ liệu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [state.selectedIndustry, state.trendDays]);

  const getTrendDirection = (): TrendDirection => {
    if (!trends || trends.daily_data.length < 2) return 'stable';

    const firstWeek = trends.daily_data.slice(0, 7);
    const lastWeek = trends.daily_data.slice(-7);

    const avgFirst = firstWeek.reduce((sum, d) => sum + d.pass_rate, 0) / firstWeek.length;
    const avgLast = lastWeek.reduce((sum, d) => sum + d.pass_rate, 0) / lastWeek.length;

    const change = avgLast - avgFirst;
    if (change > 2) return 'increasing';
    if (change < -2) return 'decreasing';
    return 'stable';
  };

  const getScoreStatusClass = (score: number): string => {
    const status = getComplianceScoreStatus(score);
    switch (status.status) {
      case 'excellent': return styles.scoreExcellent;
      case 'good': return styles.scoreGood;
      case 'warning': return styles.scoreWarning;
      case 'critical': return styles.scoreCritical;
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Đang tải dữ liệu tuân thủ SOP...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <span className={styles.errorIcon}>⚠️</span>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  const viewModes = [
    { key: 'overview', label: '📊 Tổng quan' },
    { key: 'trends', label: '📈 Xu hướng' },
    { key: 'industries', label: '🏭 Ngành nghề' },
    { key: 'sops', label: '📋 SOP' },
  ] as const;

  const industryOptions = [
    { value: 'all', label: 'Tất cả ngành nghề' },
    { value: 'F&B', label: 'Thực phẩm & Đồ uống' },
    { value: 'HOSPITALITY', label: 'Khách sạn & Nhà nghỉ' },
    { value: 'CLEANING', label: 'Dịch vụ Vệ sinh' },
    { value: 'NURSING', label: 'Chăm sóc Điều dưỡng' },
    { value: 'HEALTHCARE', label: 'Y tế' },
  ];

  const trendDayOptions = [
    { value: 7, label: '7 ngày' },
    { value: 14, label: '14 ngày' },
    { value: 30, label: '30 ngày' },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.titleBadge}>
              <h1 className={styles.title}>📋 SOP Compliance Dashboard</h1>
            </div>
            <div className={styles.roleBadge}>Operations View</div>
          </div>

          <div className={styles.headerRight}>
            <div className={styles.dateInfo}>
              <div className={styles.dateLabel}>
                {new Date().toLocaleDateString('vi-VN', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </div>
              <div className={styles.userName}>{user?.full_name || 'Operations Manager'}</div>
            </div>
            <button className={styles.logoutButton} onClick={() => logout()}>Đăng xuất</button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Filters */}
        <section className={styles.filtersSection}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Ngành nghề:</label>
            <select
              className={styles.filterSelect}
              value={state.selectedIndustry}
              onChange={(e) => setState({ ...state, selectedIndustry: e.target.value })}
            >
              {industryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Khoảng thời gian:</label>
            <select
              className={styles.filterSelect}
              value={state.trendDays}
              onChange={(e) => setState({ ...state, trendDays: Number(e.target.value) })}
            >
              {trendDayOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.viewModeButtons}>
            {viewModes.map((mode) => (
              <button
                key={mode.key}
                className={`${styles.viewModeButton} ${state.viewMode === mode.key ? styles.viewModeButtonActive : ''}`}
                onClick={() => setState({ ...state, viewMode: mode.key })}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </section>

        {/* Compliance Score Card */}
        {dashboard && (
          <section className={styles.scoreSection}>
            <div className={styles.scoreCard}>
              <div
                className={styles.scoreCircle}
                style={{
                  '--score-percent': dashboard.compliance_score,
                  '--score-color': getComplianceScoreStatus(dashboard.compliance_score).color,
                } as React.CSSProperties}
              >
                <span className={styles.scoreValue}>{dashboard.compliance_score.toFixed(1)}</span>
                <span className={styles.scoreLabel}>Điểm tuân thủ</span>
              </div>

              <div className={`${styles.scoreStatus} ${getScoreStatusClass(dashboard.compliance_score)}`}>
                {getComplianceScoreStatus(dashboard.compliance_score).label}
              </div>

              <div className={styles.scoreFormula}>
                <div className={styles.formulaTitle}>Công thức tính điểm</div>
                <div className={styles.formulaRow}>
                  <span className={styles.formulaLabel}>Tỷ lệ đạt (40%)</span>
                  <span className={styles.formulaValue}>{formatPercent(dashboard.compliance_metrics.pass_rate)}</span>
                </div>
                <div className={styles.formulaRow}>
                  <span className={styles.formulaLabel}>Độ bao phủ SOP (30%)</span>
                  <span className={styles.formulaValue}>{dashboard.sop_statistics.active_sops}/{dashboard.sop_statistics.total_sops}</span>
                </div>
                <div className={styles.formulaRow}>
                  <span className={styles.formulaLabel}>Tuân thủ thời gian (20%)</span>
                  <span className={styles.formulaValue}>-</span>
                </div>
                <div className={styles.formulaRow}>
                  <span className={styles.formulaLabel}>Đạt KPI (10%)</span>
                  <span className={styles.formulaValue}>{kpiSummary ? `${kpiSummary.total_kpis} KPIs` : '-'}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Overview View - KPI Cards */}
        {dashboard && state.viewMode === 'overview' && (
          <>
            <section className={styles.kpiSection}>
              <div className={styles.kpiGrid}>
                <div className={styles.kpiCard}>
                  <div className={styles.kpiIcon}>📋</div>
                  <div className={styles.kpiContent}>
                    <div className={styles.kpiLabel}>Tổng số SOP</div>
                    <div className={styles.kpiValue}>{dashboard.sop_statistics.total_sops}</div>
                    <div className={styles.kpiSubtext}>{dashboard.sop_statistics.active_sops} đang hoạt động</div>
                  </div>
                </div>

                <div className={styles.kpiCard}>
                  <div className={styles.kpiIcon}>✅</div>
                  <div className={styles.kpiContent}>
                    <div className={styles.kpiLabel}>Kiểm tra đạt</div>
                    <div className={styles.kpiValue}>{dashboard.compliance_metrics.pass_count}</div>
                    <div className={styles.kpiSubtext}>{formatPercent(dashboard.compliance_metrics.pass_rate)} tỷ lệ đạt</div>
                  </div>
                </div>

                <div className={styles.kpiCard}>
                  <div className={styles.kpiIcon}>❌</div>
                  <div className={styles.kpiContent}>
                    <div className={styles.kpiLabel}>Kiểm tra không đạt</div>
                    <div className={styles.kpiValue}>{dashboard.compliance_metrics.fail_count}</div>
                    <div className={styles.kpiSubtext}>{formatPercent(dashboard.compliance_metrics.fail_rate)} tỷ lệ lỗi</div>
                  </div>
                </div>

                <div className={styles.kpiCard}>
                  <div className={styles.kpiIcon}>📊</div>
                  <div className={styles.kpiContent}>
                    <div className={styles.kpiLabel}>Tổng kiểm tra</div>
                    <div className={styles.kpiValue}>{dashboard.compliance_metrics.total_checks}</div>
                    <div className={styles.kpiSubtext}>trong {state.trendDays} ngày</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Violations Summary */}
            <section className={styles.violationsSection}>
              <h2 className={styles.sectionTitle}>⚠️ Tổng hợp vi phạm</h2>
              <div className={styles.violationsGrid}>
                <div className={`${styles.violationCard} ${styles.violationCritical}`}>
                  <div className={styles.violationCount}>{dashboard.violations.critical}</div>
                  <div className={styles.violationLabel}>Nghiêm trọng</div>
                </div>
                <div className={`${styles.violationCard} ${styles.violationHigh}`}>
                  <div className={styles.violationCount}>{dashboard.violations.high}</div>
                  <div className={styles.violationLabel}>Cao</div>
                </div>
                <div className={`${styles.violationCard} ${styles.violationMedium}`}>
                  <div className={styles.violationCount}>{dashboard.violations.medium}</div>
                  <div className={styles.violationLabel}>Trung bình</div>
                </div>
                <div className={`${styles.violationCard} ${styles.violationLow}`}>
                  <div className={styles.violationCount}>{dashboard.violations.low}</div>
                  <div className={styles.violationLabel}>Thấp</div>
                </div>
              </div>
            </section>

            {/* SOP by Category */}
            <section className={styles.tableSection}>
              <h2 className={styles.sectionTitle}>📂 SOP theo danh mục</h2>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Danh mục</th>
                      <th className={styles.th}>Số lượng SOP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(dashboard.sop_statistics.by_category).map(([category, count]) => (
                      <tr key={category} className={styles.tr}>
                        <td className={styles.td}>
                          <span className={styles.categoryBadge}>{category}</span>
                        </td>
                        <td className={styles.td}>{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {/* Trends View */}
        {trends && state.viewMode === 'trends' && (
          <section className={styles.trendsSection}>
            <h2 className={styles.sectionTitle}>📈 Xu hướng tuân thủ ({trends.period_days} ngày)</h2>

            <div className={styles.trendsAnalysis}>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Xu hướng:</span>
                <span className={styles.trendValue}>
                  {getTrendDirection() === 'increasing' && '📈 Cải thiện'}
                  {getTrendDirection() === 'decreasing' && '📉 Giảm sút'}
                  {getTrendDirection() === 'stable' && '➡️ Ổn định'}
                </span>
              </div>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Tỷ lệ đạt TB:</span>
                <span className={styles.trendValue}>{formatPercent(trends.summary.avg_pass_rate)}</span>
              </div>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Tổng kiểm tra:</span>
                <span className={styles.trendValue}>{trends.summary.total_checks}</span>
              </div>
            </div>

            <div className={styles.chartContainer}>
              <div className={styles.chartGrid}>
                {trends.daily_data.slice(-14).map((day, idx) => (
                  <div key={idx} className={styles.chartBar}>
                    <span className={styles.chartBarLabel}>{formatDateVN(day.date)}</span>
                    <div
                      className={`${styles.chartBarFill} ${styles.chartBarPass}`}
                      style={{ width: `${Math.min(day.pass_rate * 3, 100)}%` }}
                    >
                      {formatPercent(day.pass_rate)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Ngày</th>
                    <th className={styles.th}>Đạt</th>
                    <th className={styles.th}>Không đạt</th>
                    <th className={styles.th}>Tổng</th>
                    <th className={styles.th}>Tỷ lệ đạt</th>
                  </tr>
                </thead>
                <tbody>
                  {trends.daily_data.slice(-14).reverse().map((day, idx) => (
                    <tr key={idx} className={styles.tr}>
                      <td className={styles.td}>{formatDateVN(day.date)}</td>
                      <td className={styles.td}>{day.pass_count}</td>
                      <td className={styles.td}>{day.fail_count}</td>
                      <td className={styles.td}>{day.total_count}</td>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${day.pass_rate >= 80 ? styles.statusBadgeSuccess : day.pass_rate >= 70 ? styles.statusBadgeWarning : styles.statusBadgeDanger}`}>
                          {formatPercent(day.pass_rate)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Industries View */}
        {coverage && state.viewMode === 'industries' && (
          <section className={styles.coverageSection}>
            <h2 className={styles.sectionTitle}>🏭 Độ bao phủ theo ngành nghề ({coverage.total_industries} ngành)</h2>

            <div className={styles.coverageGrid}>
              {coverage.industries.map((ind, idx) => (
                <div key={idx} className={styles.coverageCard}>
                  <div className={styles.coverageHeader}>
                    <span className={styles.coverageName}>{ind.industry_vi}</span>
                    <span className={styles.coverageCount}>{ind.sop_count} SOPs</span>
                  </div>
                  <div className={styles.coverageBar}>
                    <div
                      className={styles.coverageBarFill}
                      style={{ width: `${ind.coverage_percent}%` }}
                    />
                  </div>
                  <div className={styles.coveragePercent}>{formatPercent(ind.coverage_percent)}</div>
                </div>
              ))}
            </div>

            {/* Industry Distribution Table */}
            {dashboard && (
              <div className={styles.tableSection}>
                <h3 className={styles.sectionTitle}>Phân bổ SOP theo ngành</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>Ngành nghề</th>
                        <th className={styles.th}>Mã ngành</th>
                        <th className={styles.th}>Số lượng SOP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(dashboard.sop_statistics.by_industry).map(([industry, count]) => (
                        <tr key={industry} className={styles.tr}>
                          <td className={styles.td}>{INDUSTRY_NAMES_VI[industry] || industry}</td>
                          <td className={styles.td}>
                            <span className={styles.categoryBadge}>{industry}</span>
                          </td>
                          <td className={styles.td}>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {/* SOPs View */}
        {state.viewMode === 'sops' && (
          <>
            {/* Top Performing SOPs */}
            <section className={styles.tableSection}>
              <h2 className={styles.sectionTitle}>🏆 Top 5 SOP đạt cao nhất</h2>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Mã SOP</th>
                      <th className={styles.th}>Tên SOP</th>
                      <th className={styles.th}>Ngành</th>
                      <th className={styles.th}>Kiểm tra</th>
                      <th className={styles.th}>Tỷ lệ đạt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSOPs.map((sop, idx) => (
                      <tr key={idx} className={styles.tr}>
                        <td className={styles.td}><strong>{sop.sop_code}</strong></td>
                        <td className={styles.td}>{sop.name_vi}</td>
                        <td className={styles.td}>
                          {sop.industry && (
                            <span className={styles.categoryBadge}>
                              {INDUSTRY_NAMES_VI[sop.industry] || sop.industry}
                            </span>
                          )}
                        </td>
                        <td className={styles.td}>{sop.total_checks}</td>
                        <td className={styles.td}>
                          <span className={`${styles.statusBadge} ${styles.statusBadgeSuccess}`}>
                            {sop.pass_rate !== null ? formatPercent(sop.pass_rate) : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Bottom Performing SOPs */}
            <section className={styles.tableSection}>
              <h2 className={styles.sectionTitle}>⚠️ Top 5 SOP cần cải thiện</h2>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Mã SOP</th>
                      <th className={styles.th}>Tên SOP</th>
                      <th className={styles.th}>Ngành</th>
                      <th className={styles.th}>Vi phạm</th>
                      <th className={styles.th}>Tỷ lệ lỗi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bottomSOPs.map((sop, idx) => (
                      <tr key={idx} className={styles.tr}>
                        <td className={styles.td}><strong>{sop.sop_code}</strong></td>
                        <td className={styles.td}>{sop.name_vi}</td>
                        <td className={styles.td}>
                          {sop.industry && (
                            <span className={styles.categoryBadge}>
                              {INDUSTRY_NAMES_VI[sop.industry] || sop.industry}
                            </span>
                          )}
                        </td>
                        <td className={styles.td}>{sop.fail_count || 0}</td>
                        <td className={styles.td}>
                          <span className={`${styles.statusBadge} ${styles.statusBadgeDanger}`}>
                            {sop.fail_rate !== null ? formatPercent(sop.fail_rate) : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* KPI Summary */}
            {kpiSummary && (
              <section className={styles.tableSection}>
                <h2 className={styles.sectionTitle}>📊 Tổng hợp KPI</h2>
                <div className={styles.kpiGrid}>
                  <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon}>🎯</div>
                    <div className={styles.kpiContent}>
                      <div className={styles.kpiLabel}>Tổng số KPI</div>
                      <div className={styles.kpiValue}>{kpiSummary.total_kpis}</div>
                    </div>
                  </div>
                  <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon}>📋</div>
                    <div className={styles.kpiContent}>
                      <div className={styles.kpiLabel}>SOPs có KPI</div>
                      <div className={styles.kpiValue}>{kpiSummary.sops_with_kpis}</div>
                    </div>
                  </div>
                  <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon}>📈</div>
                    <div className={styles.kpiContent}>
                      <div className={styles.kpiLabel}>Mục tiêu TB</div>
                      <div className={styles.kpiValue}>{kpiSummary.avg_target.toFixed(1)}</div>
                    </div>
                  </div>
                </div>

                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>Danh mục KPI</th>
                        <th className={styles.th}>Số lượng</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(kpiSummary.by_category).map(([category, count]) => (
                        <tr key={category} className={styles.tr}>
                          <td className={styles.td}>{category}</td>
                          <td className={styles.td}>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <span>SOP Compliance Dashboard - Sprint 30</span>
          <span>{getTenantDisplayText()} | Cập nhật: {dashboard ? new Date(dashboard.last_updated).toLocaleString('vi-VN') : '-'}</span>
        </div>
      </footer>
    </div>
  );
};

export default SOPComplianceDashboard;
